import React, {useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {detachPersonnel, unlinkPersonnelAccount} from '@/entities/personnel/api';
import {fetchPersonnel} from '@/entities/personnel/api';
import {fetchPersonAttendance} from '@/entities/attendance/api';
import {PersonCard} from '@/features/view-people/ui/PersonCard';
import {PersonSubscribers} from '@/features/manage-telegram-recipients/ui/PersonSubscribers';
import {SalarySection} from '@/features/manage-salaries/ui/SalarySection';
import {SummaryTiles} from '@/features/view-my-attendance/ui/SummaryTiles';
import {AppButton} from '@/shared/ui/button';
import {SectionHeader} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {InviteSheet} from './InviteSheet';

/**
 * The employer's view of one person: the read-only card (reused from Home) plus this
 * month's attendance summary and the actions Home can't do — edit, invite to
 * self-service, delete. Delete is a two-tap confirm; a mis-tap must not remove a person
 * the terminals sync against.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(6)},
  section: {gap: spacing(2)},
  actions: {gap: spacing(2), paddingTop: spacing(2), paddingBottom: spacing(4)},
});

export function PersonnelCard({
  companyId,
  recordId,
  personName,
  onEdit,
  onDeleted,
}: {
  companyId: string;
  recordId: string;
  /// Used as the label on any Telegram invite minted from this card — the
  /// manager reading the recipients list later needs to know who it was about.
  personName: string;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  const [inviting, setInviting] = useState(false);
  const [confirmDetach, setConfirmDetach] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const month = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const attendance = useQuery({
    queryKey: ['personnel-attendance', companyId, recordId, month],
    queryFn: () =>
      fetchPersonAttendance(companyId, recordId, {kind: 'month', month}),
  });

  /**
   * Is this record claimed by an employee account? `account_id` is only on the DETAIL
   * response, which this card does not otherwise need — but it is the one thing that
   * decides whether "отвязать" means anything, and a button that appears for everyone
   * and 404s for most is worse than one extra read.
   */
  const detail = useQuery({
    queryKey: ['personnel', companyId, recordId],
    queryFn: () => fetchPersonnel(companyId, recordId),
  });

  const unlink = useMutation({
    meta: {toast: 'toast.accountUnlinked'},
    mutationFn: () => unlinkPersonnelAccount(companyId, recordId),
    onSuccess: async () => {
      setConfirmUnlink(false);
      await qc.invalidateQueries({queryKey: ['personnel', companyId, recordId]});
    },
  });

  const remove = useMutation({
    // Экран закрывается сразу после — сказать об этом успевает только тост.
    meta: {toast: 'toast.personnelDetached'},
    mutationFn: () => detachPersonnel(companyId, recordId),
    onSuccess: async () => {
      await qc.invalidateQueries({queryKey: ['personnel-list', companyId]});
      onDeleted();
    },
  });

  return (
    <View style={styles.root}>
      <PersonCard companyId={companyId} recordId={recordId} />

      <View style={styles.section}>
        <SectionHeader title={t('personnel.attendanceThisMonth')} />
        {attendance.isPending ? (
          <Skeleton height={82} radius={radii.lg} />
        ) : attendance.isError ? (
          <AppText variant="body" color={p.mutedForeground}>
            {t('attendance.error')}
          </AppText>
        ) : (
          <SummaryTiles summary={attendance.data.summary} />
        )}
      </View>

      {/* Оплата стоит ВЫШЕ телеграм-подписчиков и ниже посещаемости, потому что читается
          вместе с ней: начисленное в ведомости — это ставка, делённая на плановые дни и
          умноженная на дни явки, и две половины этой дроби должны стоять рядом.
          Раздела нет вовсе у того, у кого нет `salaries/read` — см. SalarySection. */}
      <SalarySection companyId={companyId} recordId={recordId} />

      <PersonSubscribers
        companyId={companyId}
        recordId={recordId}
        personName={personName}
      />

      <View style={styles.actions}>
        <AppButton title={t('personnel.edit')} icon="edit" onPress={onEdit} />
        <AppButton
          title={t('personnel.invite')}
          tone="soft"
          icon="link"
          onPress={() => setInviting(true)}
        />
        {/* Only for a record an employee has actually claimed — otherwise there is
            nothing to revoke and the route would answer 404. */}
        {detail.data?.account_id ? (
          confirmUnlink ? (
            <AppButton
              title={t('personnel.unlinkDo')}
              tone="destructive"
              icon="close"
              loading={unlink.isPending}
              onPress={() => unlink.mutate()}
              testID="personnel-unlink-confirm"
            />
          ) : (
            <AppButton
              title={t('personnel.unlink')}
              tone="ghost"
              onPress={() => setConfirmUnlink(true)}
              testID="personnel-unlink"
            />
          )
        ) : null}
        {/* Detach is a ghost until it is armed: on a card whose other two buttons are
            filled, a third filled button is a mis-tap waiting to happen.
            It says DETACH, not delete, because that is what the call does — the face
            goes, the identity stays findable, and the person can be put back by being
            saved under the same id. "Delete" would promise an erasure that does not
            happen and hide the way back. */}
        {confirmDetach ? (
          <AppButton
            title={t('personnel.confirmDetach')}
            tone="destructive"
            icon="logout"
            onPress={() => remove.mutate()}
            loading={remove.isPending}
          />
        ) : (
          <AppButton
            title={t('personnel.detach')}
            tone="ghost"
            onPress={() => setConfirmDetach(true)}
          />
        )}
      </View>

      <InviteSheet
        key={inviting ? 'invite-open' : 'invite-closed'}
        companyId={companyId}
        recordId={recordId}
        visible={inviting}
        onClose={() => setInviting(false)}
      />
    </View>
  );
}
